// src/Services/shareAris.js
// Share Aris (v26.1) — Companion Membership + Aris Pass invitations.
// Endpoints live on core-lambda (same base as Connections), so we reuse
// axiosRelationshipInstance. The public landing endpoint is unauthenticated,
// so it uses a bare axios call (no auth interceptor). Mirrors the try/catch +
// payload-normalize + toast pattern in Realtionship.js.
import axios from "axios";
import { axiosRelationshipInstance } from "../AxiosConfiguration";
import { showErrorToast } from "../Utils/toast";

// Strip any trailing slash so `${base}/api/share/...` never becomes `//api/...`
// (the env value has a trailing slash, which produced a 404 on the invite link).
const shareArisUrl = (process.env.REACT_APP_BASE_URL_ASSESSMENT || "").replace(/\/+$/, "");

const unwrap = (res) => res?.data?.payload ?? res?.data?.data ?? res?.data ?? null;

// GET /api/me/share-aris — all of the member's grants (every state).
export const getShareArisGrants = async () => {
  try {
    const res = await axiosRelationshipInstance.get("/api/me/share-aris");
    return { grants: unwrap(res) || [], error: null };
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || "Something went wrong";
    showErrorToast(message);
    return { grants: [], error: { status: error?.response?.status, message } };
  }
};

// POST /api/me/share-aris/invitations
// payload: { benefit_type: "companion"|"pass", first_name, last_name,
//            invited_email?, invited_phone?, connect_on_activation }
export const sendShareArisInvitation = async (payload) => {
  try {
    const res = await axiosRelationshipInstance.post(
      "/api/me/share-aris/invitations",
      payload
    );
    return { grant: unwrap(res), error: null };
  } catch (error) {
    // 403 = plan doesn't include Share Aris; 409 = slot limit reached.
    const status = error?.response?.status;
    const message = error?.response?.data?.message || error?.message || "Something went wrong";
    if (status !== 403) showErrorToast(message);
    return { grant: null, error: { status, message } };
  }
};

// DELETE /api/me/share-aris/{grantId} — cancel/revoke (frees the slot).
export const cancelShareArisGrant = async (grantId) => {
  try {
    const res = await axiosRelationshipInstance.delete(`/api/me/share-aris/${grantId}`);
    return { grant: unwrap(res), error: null };
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || "Something went wrong";
    showErrorToast(message);
    return { grant: null, error: { status: error?.response?.status, message } };
  }
};

// GET /api/share/{token} — PUBLIC recipient landing view (no auth).
export const getPublicInvitation = async (token) => {
  try {
    const res = await axios.get(`${shareArisUrl}/api/share/${token}`);
    return { invitation: unwrap(res), error: null };
  } catch (error) {
    const status = error?.response?.status;
    const message = error?.response?.data?.message || error?.message || "Invitation not found";
    return { invitation: null, error: { status, message } };
  }
};

// --- View-model helpers ------------------------------------------------------
// A grant occupies one of the member's slots while its invitation is out or
// it is active (mirrors the backend SLOT_OCCUPYING_STATUSES).
const SLOT_OCCUPYING = ["invitation_sent", "accepted_incomplete", "active", "renewal_off"];

export const splitGrants = (grants = []) => {
  const companion = grants.find((g) => g.benefit_type === "companion" && SLOT_OCCUPYING.includes(g.status)) || null;
  const passes = grants.filter((g) => g.benefit_type === "pass");
  return { companion, passes };
};

// Unused Aris Passes available to send (cap 3) — drives the menu badge.
export const availablePassCount = (grants = [], cap = 3) => {
  const used = grants.filter((g) => g.benefit_type === "pass" && SLOT_OCCUPYING.includes(g.status)).length;
  return Math.max(0, cap - used);
};
